import React, { FunctionComponent, useCallback } from 'react';
import { styled } from '@mui/material/styles';

import { UnstyledUnorderedList } from '../../navbar/common/resetUI';
import { SIDEBAR_HIGHLIGHT_COLOR } from '../constants';
import { NavDataItem } from '../types';
import NavMenuItem from './navMenuItem';
import { getAppCuesId } from 'src/utils/appCuesUtil';

interface NavItem {
  badgeLabel?: string;
  icon?: string;
  name: string;
  path?: string;
  search?: string;
  tooltip?: string;
}

interface NavGroupProps extends NavItem {
  active?: boolean;
  children?: NavDataItem[];
  onToggle?: () => void;
}

const NavGroup: FunctionComponent<NavGroupProps> = ({
  active,
  badgeLabel,
  children,
  icon,
  name,
  onToggle,
  path,
  search,
  tooltip,
}) => {
  const isExpanded = !!active;

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onToggle?.();
    },
    [onToggle],
  );

  return (
    <Container
      data-appcues-target={getAppCuesId({
        componentType: 'container',
        featureName: 'sidebar',
        pageName: 'navigation',
        subType: name,
      })}
      key={name}
      sx={{
        borderRadius: '8px',
        // padding: '8px !important',
        width: '226px',
      }}
    >
      {children ? (
        <SubMenuContainer active={isExpanded}>
          <NavMenuItem
            badgeLabel={badgeLabel}
            icon={icon}
            isTopLevel={true}
            name={name}
            onClick={handleExpandClick}
            path={children && children.length ? children[0].route?.path : path}
            search={
              children && children.length ? children[0].route?.search : search
            }
            tooltip={tooltip}
          />
          {isExpanded && (
            <UnstyledUnorderedList className='subMenu'>
              {children.map(
                ({
                  badgeLabel,
                  name: subMenuItemName,
                  notificationsLabel,
                  route: subMenuItemRoute,
                  tooltip: subMenuItemTooltip,
                }) => (
                  <li
                    data-appcues-target={getAppCuesId({
                      componentType: 'item',
                      featureName: 'sidebar',
                      pageName: 'navigation',
                      subType: name,
                    })}
                    key={subMenuItemName}
                  >
                    <NavMenuItem
                      additionalMatchPaths={
                        subMenuItemRoute?.additionalMatchPaths
                      }
                      badgeLabel={badgeLabel}
                      external={subMenuItemRoute?.external}
                      minimized={false}
                      name={subMenuItemName}
                      notificationsBadgeLabel={notificationsLabel}
                      path={subMenuItemRoute?.path}
                      search={subMenuItemRoute?.search}
                      tooltip={subMenuItemTooltip}
                    />
                  </li>
                ),
              )}
            </UnstyledUnorderedList>
          )}
        </SubMenuContainer>
      ) : (
        <NavMenuItem
          badgeLabel={badgeLabel}
          icon={icon}
          isTopLevel={true}
          name={name}
          onClick={handleExpandClick}
          path={path}
          search={search}
        />
      )}
    </Container>
  );
};

const Container = styled('li')`
  position: relative;
  left: 4px;
`;

export default NavGroup;

const SubMenuContainer = styled('div')<{ active?: boolean }>`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  margin: 0 auto ${props => (props.active ? '4px' : '0')};
  background-color: ${props =>
    props.active ? SIDEBAR_HIGHLIGHT_COLOR : 'transparent'};

  ${props =>
    !props.active
      ? `
  &:hover {
    /* when hovering a non-active group, keep the top corners slightly rounded (4px)
       so the hover background appears with a 4px radius */
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  `
      : ''}

  ${props =>
    props.active
      ? `
    opacity: 1 !important;

    & > a,
    & > div,
    .subMenu a {
      opacity: 1 !important;
      color: var(--color-solid-white);
    }
  `
      : ''}

  ${props =>
    props.active
      ? `
    & > a, & > div {
      margin-bottom: 0 !important;
    }
  `
      : ''}

  .subMenu {
    margin: 0 auto;
    /* give a little inset so active child backgrounds don't touch the container edges */
    padding: 2px 0px 6px 0px;
    width: 210px;

    li {
      text-align: left;
      margin-bottom: 4px;
    }

    a {
      display: block;
      border-radius: 6px;
      color: white;
      text-decoration: none;
    }

    a.active {
      background-color: ${props => props.theme.palette.colors.teal[500]};
      color: var(--color-solid-white);
    }

    a:hover {
      background-color: ${props => props.theme.palette.colors.teal[600]};
    }
  }
`;
